import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { binService } from "../../services/binService";
import { bankService } from "../../services/bankService";
import { saleService } from "../../services/saleService";
import { subscribeBinUpdates } from "../../services/binSocket";
import ChatModal from "../Chat/ChatModal";
import AgentPreview from "../Agent/AgentPreview";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  CubeIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

const Sell = () => {
  const navigate = useNavigate();
  const [binData, setBinData] = useState({ items: [], total_price: 0 });
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [loading, setLoading] = useState(true);
  const [selling, setSelling] = useState(false);
  const [message, setMessage] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [chatBinId, setChatBinId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [binResponse, banksResponse] = await Promise.all([
        binService.getUserBin(),
        bankService.getUserBanks(),
      ]);
      
      // Extract bin data from the API response
      const binData = binResponse?.data?.bin || binResponse?.bin || {
        materials: [],
        totalPrice: 0,
        totalQuantity: 0,
        validationStatus: false
      };
      
      setBinData(binData);
      setBanks(banksResponse);

      const bankDetails =
        banksResponse?.data?.bankDetails || banksResponse?.bankDetails || [];
      setSelectedBank((prev) => {
        if (bankDetails.length > 0 && !prev) {
          return bankDetails[0]._id;
        }
        return prev;
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      setMessage("Error loading data");
      // Set empty bin data on error
      setBinData({
        materials: [],
        totalPrice: 0,
        totalQuantity: 0,
        validationStatus: false
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Live updates when agent validates the bin (server emits bin:updated to the seller). */
  useEffect(() => {
    const unsub = subscribeBinUpdates((bin) => {
      if (!bin) {
        setBinData({
          materials: [],
          totalPrice: 0,
          totalQuantity: 0,
          validationStatus: false,
        });
        return;
      }
      setBinData(bin);
    });
    return unsub;
  }, []);

  /** Refetch when user returns to the tab (e.g. after agent validated in another window). */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchData();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchData]);

  const isBinValidated =
    binData?.validationStatus === true || binData?.validationStatus === "true";

  const handleSell = async () => {
    if (!selectedBank) {
      setMessage("Please select a bank account for payment. If you don't have any bank accounts, please add one first.");
      return;
    }

    // Validate that the selected bank exists
    const bankDetails = banks?.data?.bankDetails || banks?.bankDetails || [];
    const selectedBankExists = bankDetails.some(bank => bank._id === selectedBank);
    if (!selectedBankExists) {
      setMessage("Selected bank account is no longer valid. Please select a different bank account.");
      return;
    }

    if (!isBinValidated) {
      setMessage("Your bin must be validated by an agent before you can sell");
      return;
    }

    setSelling(true);
    setMessage("");

    try {
      await saleService.createSale({
        binId: binData?._id,
        bankId: selectedBank,
      });
      setBinData({
        materials: [],
        totalPrice: 0,
        totalQuantity: 0,
        validationStatus: false,
        selectedAgent: null,
        items: [],
      });
      if (typeof window !== "undefined" && window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
      setMessage("Sale completed successfully! Redirecting to sale history...");
      setTimeout(() => {
        navigate("/sales");
      }, 2000);
    } catch (error) {
      console.error("Error creating sale:", error);
      setMessage(error.response?.data?.message || "Error completing sale");
    } finally {
      setSelling(false);
    }
  };

  const handleChatClick = (binId) => {
    setChatBinId(binId);
    setShowChat(true);
  };

  const handleClearBin = async () => {
    if (window.confirm("Are you sure you want to clear your bin? This will cancel your order and remove all materials.")) {
      try {
        setLoading(true);
        await binService.clearBin();
        setMessage("Bin cleared successfully! Redirecting to materials...");
        setTimeout(() => {
          navigate("/materials");
        }, 2000);
      } catch (error) {
        console.error("Error clearing bin:", error);
        setMessage(error.response?.data?.message || "Error clearing bin");
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Complete Your Sale
        </h1>
        <p className="text-gray-600">
          Review your validated materials and select a payment account to
          complete your sale.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Agent Preview */}
      {binData?.selectedAgent && (
        <div>
          <AgentPreview 
            agent={binData.selectedAgent}
            onChatClick={handleChatClick}
            binId={binData?._id}
          />
          {/* Change Agent Button */}
          {/* {binData?.validationStatus && (
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/agents")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Change Agent
              </button>
            </div>
          )} */}
        </div>
      )}

      {/* Validation Status */}
      {!binData?.validationStatus && !binData?.selectedAgent && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-900">
                  No Agent Selected
                </h3>
                <p className="text-yellow-800">
                  Please select an agent to validate your materials before you can complete your sale.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/agents")}
              className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Select Agent
            </button>
          </div>
        </div>
      )}

      {/* Empty Bin */}
      {!binData?.materials?.length ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CubeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No materials to sell
          </h2>
          <p className="text-gray-600 mb-6">
            Add materials to your bin and get them validated by an agent first.
          </p>
          <button
            onClick={() => navigate("/materials")}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Browse Materials
          </button>
        </div>
      ) : (
        <>
          {/* Materials Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Validated Materials
            </h2>
            <div className="space-y-4">
              {binData?.materials?.map((item) => (
                <div
                  key={item._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    {item.material?.image ? (
                      <img
                        src={item.material.image}
                        alt={item.material.name}
                        className="h-12 w-12 object-cover rounded-md"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-gray-200 rounded-md flex items-center justify-center">
                        <span className="text-gray-500 text-xs">No Image</span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {item.material?.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.material?.category
                          ?.replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {item.quantity}kg × ₦
                      {item.material?.pricePerKg?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-lg font-semibold text-green-600">
                      ₦
                      {(
                        (item.quantity || 0) * (item.material?.pricePerKg || 0)
                      ).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Total Quantity: {binData?.totalQuantity}
                    kg
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-green-600">
                    ₦{binData?.totalPrice?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-sm text-gray-600">Total Amount</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Account Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Select Payment Account
            </h2>

            {banks?.data?.bankDetails?.length === 0 ? (
              <div className="text-center py-8">
                <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No bank accounts found</p>
                <button
                  onClick={() => navigate("/banks")}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Add Bank Account
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {banks?.data?.bankDetails?.map((bank) => (
                  <label
                    key={bank._id}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedBank === bank._id
                        ? 'border-green-300 bg-green-50 hover:bg-green-100'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bank"
                      value={bank._id}
                      checked={selectedBank === bank._id}
                      onChange={(e) => setSelectedBank(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">
                        {bank.bankName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {bank.accountName} - {bank.accountNumber}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          {(banks?.data?.bankDetails?.length === 0 && binData?.materials) && (
            <div>
              <p>
                It looks like you haven’t added any bank details yet. To
                complete your payment smoothly, please add your bank account
                information.
              </p>
              <button
                onClick={() => navigate("/banks")}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Add Bank Account
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {/* Proceed to Sell Button */}
              <button
                onClick={handleSell}
                disabled={
                  selling ||
                  !selectedBank ||
                  !isBinValidated ||
                  binData?.sold
                }
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-md transition-colors"
              >
                {selling ? "Completing Sale..." : "Proceed to Sell"}
              </button>

              
            </div>

            {!isBinValidated && (
              <p className="text-sm text-gray-600 mt-4 text-center">
                Your bin must be validated by an agent before you can complete
                the sale.
              </p>
            )}
          </div>

          {/* Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Sale Process
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Review your validated materials and quantities above</li>
              <li>Select the bank account where you want to receive payment</li>
              <li>Click "Complete Sale" to finalize your transaction</li>
              <li>
                Payment will be processed and sent to your selected bank account
              </li>
              <li>You can track payment status in your sale history</li>
            </ol>
          </div>
        </>
      )}

      {/* Chat Modal */}
      <ChatModal 
        binId={chatBinId} 
        isOpen={showChat} 
        onClose={() => setShowChat(false)} 
      />
    </div>
  );
};

export default Sell;
